import braintree from "braintree-web";
import type { ApplePay, ApplePayPayload } from "braintree-web";
import { BraintreeContext } from "v3/context/braintree";
import { useEffect, useRef, useContext, useCallback } from "react";
import { logClientSideError } from "@pepdirect/v3/helpers/logAndCaptureInSentry";
import { CheckoutSessionFragment } from "v3/services/graphql/generated";

type UseApplePayOptions = {
  onApprove?: (options: ApplePayPayload) => Promise<void>;
  checkoutSession: CheckoutSessionFragment;
  title: string;
};

type UseApplePayReturnType = {
  createPaymentRequest: () => void;
};

const useApplePay = (options: UseApplePayOptions): UseApplePayReturnType => {
  const { onApprove, checkoutSession, title } = options;
  const { client } = useContext(BraintreeContext);
  const applePayRef = useRef<ApplePay>();

  useEffect(() => {
    (async () => {
      if (!client) return;

      try {
        const applePayInstance = await braintree.applePay.create({
          client: client,
        });

        applePayRef.current = applePayInstance;
      } catch (e) {
        logClientSideError(e);
      }
    })();
  }, [client]);

  const createPaymentRequest = useCallback(() => {
    const shipping = checkoutSession?.price?.shipping / 100 || 0;
    const subtotal = checkoutSession?.price?.subtotal / 100 || 0;
    const discount = -checkoutSession?.price?.discount / 100 || 0;

    const lineItems = [
      {
        label: "Subtotal",
        type: "final",
        amount: subtotal.toString(),
      },
      {
        label: "Shipping & Handling",
        type: "final",
        amount: shipping.toString(),
      },
      {
        label: "Estimated Sales Tax",
        type: "pending",
        amount: "0",
      },
    ];
    if (discount < 0) {
      lineItems.push({
        label: "Discount",
        type: "final",
        amount: discount.toString(),
      });
    }
    const paymentRequest = applePayRef.current?.createPaymentRequest({
      total: {
        label: title,
        // @ts-expect-error incorrect types
        type: "pending",
        amount: "0",
      },
      lineItems,
      requiredBillingContactFields: ["postalAddress"],
    }) as ApplePayJS.ApplePayPaymentRequest;

    if (!paymentRequest) {
      return;
    }

    let session: ApplePaySession;
    try {
      session = new ApplePaySession(3, paymentRequest);
    } catch (error) {
      logClientSideError(error);
      return;
    }

    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await applePayRef.current?.performValidation({
          validationURL: event.validationURL,
          displayName: title,
        });
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        logClientSideError(error);
        return;
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const payload = await applePayRef.current?.tokenize({
          token: event.payment.token,
        });
        if (payload) {
          onApprove?.(payload);
        }

        session.completePayment(ApplePaySession.STATUS_SUCCESS);
      } catch (error) {
        logClientSideError(error);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    };

    session.begin();
  }, [checkoutSession, onApprove, title]);

  return {
    createPaymentRequest,
  };
};

export default useApplePay;
